import type { AuthChangeEvent, Session, User } from "@supabase/supabase-js";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import type { AppRole, EmployeeStatus } from "./auth";
import { fetchRoleForUserId, isAdminRole, signOut as authSignOut } from "./auth";
import { identifyUser } from "./analytics";
import { setSentryUser } from "./sentry";
import { supabase } from "./supabase";

type AuthContextValue = {
  user: User | null;
  role: AppRole | null;
  employeeStatus: EmployeeStatus | null;
  adminPermissions: string[] | null;
  /** When role is admin, whether MFA is required (defaults true if column missing). */
  mfaRequired: boolean;
  loading: boolean;
  signOut: () => Promise<void>;
  refetchProfile: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Treat two User objects as the "same user" for context purposes.
 *
 * Supabase's tab-visibility token refresh emits SIGNED_IN / TOKEN_REFRESHED with a
 * brand-new `session.user` object reference whose meaningful fields are identical
 * to what we already have. Calling `setUser` with that fresh ref would flip the
 * memoized context value, cascade through every `useAuth()` consumer, and re-fire
 * every downstream `[user]`-dep effect - which the user perceives as a "page
 * reload flash" on tab regain. Comparing by id + updated_at + email keeps the
 * context value referentially stable across those events while still picking up
 * real USER_UPDATED changes (profile rename, email change, etc.) where
 * `updated_at` actually moves.
 */
function isSameUserIdentity(a: User | null, b: User | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.id === b.id && a.updated_at === b.updated_at && a.email === b.email;
}

async function fetchEmployeeFields(userId: string, role: AppRole | null): Promise<{
  employeeStatus: EmployeeStatus | null;
  adminPermissions: string[] | null;
  mfaRequired: boolean;
}> {
  // mfa_required is meaningful for every role now - admins are mandatory (default
  // true), non-admins are opt-in (default false, owned by the user via Settings).
  // employee_status / admin_permissions are still admin-only concepts.
  const isAdmin = isAdminRole(role);
  const { data, error } = await supabase
    .from("user_profiles")
    .select("employee_status, admin_permissions, mfa_required")
    .eq("id", userId)
    .maybeSingle();

  if (error || !data) {
    // On read failure: be safe - admins default to required, others to not required.
    return { employeeStatus: null, adminPermissions: null, mfaRequired: isAdmin };
  }

  const es = data.employee_status;
  const status: EmployeeStatus | null =
    isAdmin && (es === "pending" || es === "active" || es === "rejected") ? es : null;
  const perms = data.admin_permissions;
  const adminPermissions = isAdmin && Array.isArray(perms) ? (perms as string[]) : null;
  // Admins: anything other than explicit `false` counts as required.
  // Everyone else: must be explicitly `true` to count as required.
  const mfaRequired = isAdmin ? data.mfa_required !== false : data.mfa_required === true;
  return { employeeStatus: status, adminPermissions, mfaRequired };
}

async function loadUserAndRole(): Promise<{
  user: User | null;
  role: AppRole | null;
  employeeStatus: EmployeeStatus | null;
  adminPermissions: string[] | null;
  mfaRequired: boolean;
}> {
  const { data: sessionData } = await supabase.auth.getSession();
  const user = sessionData.session?.user ?? null;
  if (!user) {
    return { user: null, role: null, employeeStatus: null, adminPermissions: null, mfaRequired: false };
  }
  const r = await fetchRoleForUserId(user.id);
  const extras = await fetchEmployeeFields(user.id, r);
  return { user, role: r, ...extras };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<AppRole | null>(null);
  const [employeeStatus, setEmployeeStatus] = useState<EmployeeStatus | null>(null);
  const [adminPermissions, setAdminPermissions] = useState<string[] | null>(null);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [loading, setLoading] = useState(true);

  const applySession = useCallback(async (opts: { initial?: boolean } = {}) => {
    if (opts.initial) setLoading(true);
    try {
      const next = await loadUserAndRole();
      setUser(next.user);
      setRole(next.role);
      setEmployeeStatus(next.employeeStatus);
      setAdminPermissions(next.adminPermissions);
      setMfaRequired(next.mfaRequired);
    } finally {
      if (opts.initial) setLoading(false);
    }
  }, []);

  // Track which user identity we've already loaded role/employee fields for.
  // Supabase emits auth events not just on real sign-in/out, but also on tab
  // visibility changes, token rotations, and SDK lifecycle events. Re-fetching
  // role/employee/permissions on every event creates fresh object references
  // that cascade re-renders across the tree, which users see as a visible
  // "refresh" flash. We only do the heavy re-fetch when the user identity
  // actually changes.
  const loadedUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    let mounted = true;

    void (async () => {
      setLoading(true);
      try {
        const next = await loadUserAndRole();
        if (!mounted) return;
        loadedUserIdRef.current = next.user?.id ?? null;
        setUser(next.user);
        setRole(next.role);
        setEmployeeStatus(next.employeeStatus);
        setAdminPermissions(next.adminPermissions);
        setMfaRequired(next.mfaRequired);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    const { data: sub } = supabase.auth.onAuthStateChange((event: AuthChangeEvent, session: Session | null) => {
      if (!mounted) return;
      if (event === "SIGNED_OUT") {
        loadedUserIdRef.current = null;
        setUser(null);
        setRole(null);
        setEmployeeStatus(null);
        setAdminPermissions(null);
        setMfaRequired(false);
        return;
      }
      if (event === "TOKEN_REFRESHED") {
        // Token rotated but identity hasn't. Only update if the user object's
        // meaningful fields actually changed - otherwise keep the existing
        // reference so the context value stays stable and we don't trigger a
        // tree-wide re-render on every tab focus.
        const nextUser = session?.user ?? null;
        setUser((prev) => (isSameUserIdentity(prev, nextUser) ? prev : nextUser));
        return;
      }

      // For all other events (SIGNED_IN, INITIAL_SESSION, USER_UPDATED, etc.),
      // skip the heavy re-fetch if the user identity is the same as what we
      // already loaded. This is the common case on tab focus regain - Supabase
      // re-emits SIGNED_IN with a fresh session.user object whenever the tab's
      // visibilitychange fires. We compare meaningful fields and keep the prior
      // reference when nothing changed, so consumers don't re-render.
      const nextUserId = session?.user?.id ?? null;
      if (nextUserId && nextUserId === loadedUserIdRef.current) {
        const nextUser = session?.user ?? null;
        setUser((prev) => (isSameUserIdentity(prev, nextUser) ? prev : nextUser));
        return;
      }

      // Identity actually changed (or first time we see one) - re-load everything.
      loadedUserIdRef.current = nextUserId;
      void (async () => {
        const next = await loadUserAndRole();
        if (!mounted) return;
        loadedUserIdRef.current = next.user?.id ?? null;
        setUser(next.user);
        setRole(next.role);
        setEmployeeStatus(next.employeeStatus);
        setAdminPermissions(next.adminPermissions);
        setMfaRequired(next.mfaRequired);
      })();
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const signOut = useCallback(async () => {
    setLoading(true);
    try {
      await authSignOut();
    } finally {
      setUser(null);
      setRole(null);
      setEmployeeStatus(null);
      setAdminPermissions(null);
      setMfaRequired(false);
      setLoading(false);
    }
  }, []);

  const refetchProfile = useCallback(async () => {
    const next = await loadUserAndRole();
    setUser(next.user);
    setRole(next.role);
    setEmployeeStatus(next.employeeStatus);
    setAdminPermissions(next.adminPermissions);
    setMfaRequired(next.mfaRequired);
  }, []);

  // Push identity into Sentry + PostHog whenever it changes. Privacy
  // boundary: only id / email / role — never anything sensitive. Both
  // helpers no-op when the underlying SDK isn't initialized (missing
  // env vars in dev / preview), so this is safe to wire unconditionally.
  // We watch (user.id, role) only — the full user object reference flips
  // on token refresh and would re-identify needlessly.
  const userId = user?.id ?? null;
  const userEmail = user?.email ?? undefined;
  useEffect(() => {
    if (!userId) {
      setSentryUser(null);
      identifyUser(null);
      return;
    }
    const identity = { id: userId, email: userEmail, role: role ?? undefined };
    setSentryUser(identity);
    identifyUser(identity);
  }, [userId, userEmail, role]);

  // Heartbeat for the analytics "active recently" signal: a cheap "I'm here"
  // ping that stamps user_profiles.last_seen_at while a session is open. Fires
  // on sign-in, every 60s, and on tab refocus (web). Fully best-effort.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    const ping = () => {
      void supabase.rpc("heartbeat").then(
        () => {},
        () => {},
      );
    };
    ping();
    const timer = setInterval(() => {
      if (!cancelled) ping();
    }, 60_000);
    let onVis: (() => void) | null = null;
    if (typeof document !== "undefined") {
      onVis = () => {
        if (document.visibilityState === "visible") ping();
      };
      document.addEventListener("visibilitychange", onVis);
    }
    return () => {
      cancelled = true;
      clearInterval(timer);
      if (onVis && typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVis);
      }
    };
  }, [userId]);

  const value = useMemo(
    () => ({
      user,
      role,
      employeeStatus,
      adminPermissions,
      mfaRequired,
      loading,
      signOut,
      refetchProfile,
    }),
    [user, role, employeeStatus, adminPermissions, mfaRequired, loading, signOut, refetchProfile]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
